import {
  useEffect as _useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  Button,
  Textarea,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  ButtonIcon,
} from '@optechai/design-system'
import { useFetcher } from '@remix-run/react'
import { Paperclip, SendIcon, StickyNoteIcon } from 'lucide-react'

const useEffect = typeof window !== 'undefined' ? useLayoutEffect : _useEffect

/**
 * __SendBar__
 *
 * Bottom input pane of chat interface.
 */
export const SendBar = ({
  error,
  allowEmptySubmit,
  allowPostingInternalNotes,
}: {
  error?: string
  allowEmptySubmit: boolean
  allowPostingInternalNotes?: boolean
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const { submit } = useFetcher({ key: 'sendChat' })
  const fileAttachmentRef = useRef<HTMLInputElement>(null)
  const [attachmentList, setAttachmentList] = useState<File[]>([])

  useEffect(() => {
    function listener() {
      if (!textAreaRef.current) return
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px'
    }
    const $el = textAreaRef.current

    if ($el) {
      $el.addEventListener('input', listener)
    }
    return () => {
      if ($el) {
        $el.removeEventListener('input', listener)
      }
    }
  }, [])

  const resetFileState = useCallback(() => {
    requestAnimationFrame(() => {
      if (fileAttachmentRef.current) {
        setAttachmentList([])
        fileAttachmentRef.current.value = ''
      }

      if (textAreaRef.current) {
        textAreaRef.current.style.height = 'auto'
        textAreaRef.current.value = ''
      }
    })
  }, [])

  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (error) return

      if (e.shiftKey) return

      if (e.key === 'Enter') {
        if (!e.currentTarget.value.trim() && !allowEmptySubmit) {
          e.preventDefault()
          return
        }

        submit(e.currentTarget.form as HTMLFormElement, {
          preventScrollReset: true,
        })
        resetFileState()
      }
    },
    [error, allowEmptySubmit, resetFileState, submit],
  )

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!textAreaRef.current?.value.trim() && !allowEmptySubmit) {
        e.preventDefault()
        return
      }

      if (error) {
        e.preventDefault()
        return
      }

      resetFileState()
    },
    [error, allowEmptySubmit, resetFileState],
  )

  return (
    <>
      {attachmentList.length ? (
        <div className="flex w-full items-center border-l border-r border-t border-border bg-surface p-m">
          <Text variant="p-s">{attachmentList.length} file included</Text>
        </div>
      ) : null}
      <div
        className="flex items-end justify-between gap-x-xs overflow-hidden border border-t border-border bg-surface p-l"
        data-component="SendBar"
      >
        <Textarea
          appearance="chromeless"
          error={error}
          label="message"
          name="message"
          onKeyDown={onKeyDownHandler}
          placeholder="Start a conversation..."
          ref={textAreaRef}
          resize="none"
          rows={1}
        />
        <input
          className="sr-only"
          name={attachmentList.length ? 'file' : undefined}
          onChange={(e) => {
            if (e.target.files) {
              setAttachmentList([
                ...attachmentList,
                ...Array.from(e.target.files),
              ])
            }
          }}
          ref={fileAttachmentRef}
          type="file"
        />
        <ButtonIcon
          icon={Paperclip}
          label="Attach a file"
          onClick={(e) => {
            e.preventDefault()
            fileAttachmentRef.current?.click()
          }}
          type="button"
        />
        {allowPostingInternalNotes && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="min-w-min"
                leftIcon={StickyNoteIcon}
                name="type"
                onClick={onClickHandler}
                size="small"
                title="Send internal note"
                value="INTERNAL_USER_NOTE"
                variant="secondary"
              />
            </TooltipTrigger>
            <TooltipContent>
              <Text variant="p-s">Send internal note</Text>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="min-w-min"
              leftIcon={SendIcon}
              name="type"
              onClick={onClickHandler}
              size="small"
              title="Send message"
              value="CUSTOMER"
              variant="primary"
            />
          </TooltipTrigger>
          <TooltipContent>
            <Text variant="p-s">Send message</Text>
          </TooltipContent>
        </Tooltip>
      </div>
    </>
  )
}
